import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbox } from "@/components/Lightbox";
import { 
    Ticket, Plus, Minus, ShoppingCart, ArrowRight, ArrowLeft,
    User, Mail, Phone, CheckCircle, AlertCircle, Loader2, Calendar, MapPin
} from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TicketShop = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const [event, setEvent] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [cart, setCart] = useState({}); // { ticket_id: { quantity, attendees: [] } }
    const [step, setStep] = useState(1); // 1: Select, 2: Attendees, 3: Customer Info, 4: Payment
    const [customerInfo, setCustomerInfo] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone: ''
    });
    const [deliveryMethod, setDeliveryMethod] = useState('pickup'); // 'pickup' | 'shipping' (nur bei versandfähigem physischem Warenkorb)
    const [shippingAddress, setShippingAddress] = useState({ name: '', street: '', zip: '', city: '', addon: '' });
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [firstTicketPrefilled, setFirstTicketPrefilled] = useState(false);
    const [flyerOpen, setFlyerOpen] = useState(false);
    // Spamschutz NUR für den kostenlosen Anmelde-Pfad (siehe handleCheckout):
    // 'website' ist ein Honeypot (für echte Nutzer unsichtbar), formLoadedAt
    // dient als Time-Trap. Beide werden ausschließlich an /register-free gesendet.
    const [website, setWebsite] = useState('');
    const [formLoadedAt] = useState(() => Date.now());

    useEffect(() => {
        fetchData();
        checkLoggedInUser();
    }, [eventId]);

    const checkLoggedInUser = async () => {
        const token = localStorage.getItem('booking_token');
        if (token) {
            try {
                const res = await axios.get(`${API_URL}/api/booking/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setLoggedInUser(res.data);
                // Pre-fill customer info with logged-in user data
                setCustomerInfo({
                    email: res.data.email || '',
                    first_name: res.data.first_name || '',
                    last_name: res.data.last_name || '',
                    phone: res.data.phone || ''
                });
            } catch (err) {
                console.log('User not logged in or token expired');
            }
        }
    };

    const fetchData = async () => {
        try {
            const [eventRes, ticketsRes] = await Promise.all([
                axios.get(`${API_URL}/api/events/${eventId}`),
                axios.get(`${API_URL}/api/tickets/event/${eventId}`)
            ]);
            setEvent(eventRes.data);
            setTickets(ticketsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Event nicht gefunden');
        } finally {
            setLoading(false);
        }
    };

    // Pre-fill first attendee with logged-in user data when cart changes
    useEffect(() => {
        if (loggedInUser && !firstTicketPrefilled) {
            const cartEntries = Object.entries(cart);
            if (cartEntries.length > 0) {
                const [firstTicketId, firstItem] = cartEntries[0];
                if (firstItem.quantity > 0 && firstItem.attendees[0]) {
                    // Only pre-fill if first attendee is empty
                    const firstAtt = firstItem.attendees[0];
                    if (!firstAtt.first_name && !firstAtt.last_name && !firstAtt.email) {
                        setCart(prev => ({
                            ...prev,
                            [firstTicketId]: {
                                ...prev[firstTicketId],
                                attendees: prev[firstTicketId].attendees.map((att, i) => 
                                    i === 0 ? {
                                        first_name: loggedInUser.first_name || '',
                                        last_name: loggedInUser.last_name || '',
                                        email: loggedInUser.email || ''
                                    } : att
                                )
                            }
                        }));
                        setFirstTicketPrefilled(true);
                    }
                }
            }
        }
    }, [cart, loggedInUser, firstTicketPrefilled]);

    const updateQuantity = (ticketId, delta) => {
        setCart(prev => {
            const current = prev[ticketId] || { quantity: 0, attendees: [] };
            const newQty = Math.max(0, current.quantity + delta);
            
            // Check availability
            const ticket = tickets.find(t => t.id === ticketId);
            if (ticket?.available_quantity !== null && newQty > ticket.available_quantity) {
                return prev;
            }
            
            // Adjust attendees array
            const attendees = [...current.attendees];
            while (attendees.length < newQty) {
                // For the first ticket overall, pre-fill with logged-in user
                const isFirstTicketEver = Object.values(prev).reduce((sum, item) => sum + item.quantity, 0) === 0 && attendees.length === 0;
                if (isFirstTicketEver && loggedInUser) {
                    attendees.push({ 
                        first_name: loggedInUser.first_name || '', 
                        last_name: loggedInUser.last_name || '', 
                        email: loggedInUser.email || '' 
                    });
                    setFirstTicketPrefilled(true);
                } else {
                    attendees.push({ first_name: '', last_name: '', email: '' });
                }
            }
            while (attendees.length > newQty) {
                attendees.pop();
            }
            
            if (newQty === 0) {
                const { [ticketId]: _, ...rest } = prev;
                return rest;
            }
            
            return { ...prev, [ticketId]: { quantity: newQty, attendees } };
        });
    };

    const updateAttendee = (ticketId, index, field, value) => {
        setCart(prev => ({
            ...prev,
            [ticketId]: {
                ...prev[ticketId],
                attendees: prev[ticketId].attendees.map((att, i) => 
                    i === index ? { ...att, [field]: value } : att
                )
            }
        }));
    };

    const getTotalItems = () => Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);
    
    const getTotalPrice = () => {
        return Object.entries(cart).reduce((sum, [ticketId, item]) => {
            const ticket = tickets.find(t => t.id === ticketId);
            return sum + (ticket?.current_price || 0) * item.quantity;
        }, 0);
    };

    // Physischer Warenkorb (Abholung): mind. ein Ticket-Typ mit ticket_kind 'physisch'.
    // Single-Mode wird serverseitig erzwungen (Mixed-Cart -> 400); hier nur für die UX.
    const isPhysicalCart = Object.keys(cart).some((id) => {
        const t = tickets.find((x) => x.id === id);
        return t?.ticket_kind === 'physisch';
    });

    // Versand nur, wenn ALLE Items physisch UND allow_shipping (gleiche Regel wie der Backend-Guard).
    const canShip = Object.keys(cart).length > 0 && Object.keys(cart).every((id) => {
        const t = tickets.find((x) => x.id === id);
        return t?.ticket_kind === 'physisch' && t?.allow_shipping;
    });
    const SHIPPING_FEE = 2.99;
    const isShipping = canShip && deliveryMethod === 'shipping';
    const shippingFee = isShipping ? SHIPPING_FEE : 0;

    const validateStep2 = () => {
        // Collect all attendees for duplicate check
        const allAttendees = [];
        
        for (const [ticketId, item] of Object.entries(cart)) {
            for (const att of item.attendees) {
                if (!att.first_name.trim() || !att.last_name.trim() || !att.email.trim()) {
                    return { valid: false, error: 'Bitte füllen Sie alle Teilnehmerfelder aus.' };
                }
                // Basic email validation
                if (!att.email.includes('@')) {
                    return { valid: false, error: 'Bitte geben Sie gültige E-Mail-Adressen ein.' };
                }
                
                // Store attendee for duplicate check
                allAttendees.push({
                    key: `${att.first_name.trim().toLowerCase()}_${att.last_name.trim().toLowerCase()}_${att.email.trim().toLowerCase()}`,
                    name: `${att.first_name} ${att.last_name}`
                });
            }
        }
        
        // Check for duplicate attendees (only if more than 1 ticket)
        if (allAttendees.length > 1) {
            const seen = new Set();
            for (const att of allAttendees) {
                if (seen.has(att.key)) {
                    return { 
                        valid: false, 
                        error: `Jedes Ticket muss für eine andere Person sein. "${att.name}" wurde mehrfach angegeben.`
                    };
                }
                seen.add(att.key);
            }
        }
        
        return { valid: true, error: null };
    };

    const validateStep3 = () => {
        if (!(customerInfo.email && customerInfo.first_name && customerInfo.last_name)) return false;
        // Bei Versand zusätzlich Adresse Pflicht (spiegelt den Backend-Guard für die Button-Aktivierung).
        if (isShipping) {
            const zipOk = /^\d{5}$/.test((shippingAddress.zip || '').trim());
            return Boolean(shippingAddress.street.trim() && shippingAddress.city.trim() && zipOk);
        }
        return true;
    };

    const handleCheckout = async () => {
        setCheckoutLoading(true);
        setError(null);
        
        try {
            const items = Object.entries(cart).map(([ticketId, item]) => ({
                ticket_type_id: ticketId,
                quantity: item.quantity,
                // Physische Tickets brauchen keine Teilnehmerdaten (Backend lockert den Zwang konditional).
                attendees: isPhysicalCart ? [] : item.attendees
            }));
            
            // Kostenloser Pfad (Gesamtpreis 0): getrennte Route OHNE Stripe.
            // Versand (mit Gebühr) ist nie kostenlos → läuft immer über Stripe.
            if (getTotalPrice() + shippingFee === 0) {
                const freeResponse = await axios.post(`${API_URL}/api/tickets/register-free`, {
                    event_id: eventId,
                    items,
                    customer_email: customerInfo.email,
                    customer_first_name: customerInfo.first_name,
                    customer_last_name: customerInfo.last_name,
                    customer_phone: customerInfo.phone || null,
                    website,                      // Honeypot (leer bei echten Nutzern)
                    form_loaded_at: formLoadedAt  // Time-Trap
                });
                navigate(`/tickets/success?order_id=${freeResponse.data.order_id}&free=1`);
                return;
            }

            // Bezahlpfad (unverändert): Stripe-Checkout
            const response = await axios.post(`${API_URL}/api/tickets/checkout`, {
                event_id: eventId,
                items,
                customer_email: customerInfo.email,
                customer_first_name: customerInfo.first_name,
                customer_last_name: customerInfo.last_name,
                customer_phone: customerInfo.phone || null,
                origin_url: window.location.origin,
                delivery_method: isShipping ? 'shipping' : 'pickup',
                shipping_name: isShipping ? (shippingAddress.name || null) : null,
                shipping_street: isShipping ? shippingAddress.street : null,
                shipping_zip: isShipping ? shippingAddress.zip : null,
                shipping_city: isShipping ? shippingAddress.city : null,
                shipping_addon: isShipping ? (shippingAddress.addon || null) : null
            });

            // Redirect to Stripe checkout
            window.location.href = response.data.checkout_url;
            
        } catch (error) {
            console.error('Checkout error:', error);
            setError(error.response?.data?.detail || 'Checkout fehlgeschlagen');
            setCheckoutLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Card className="max-w-md">
                    <CardContent className="p-8 text-center">
                        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                        <p className="text-lg">{error}</p>
                        <Button onClick={() => {
                            navigate('/');
                            setTimeout(() => {
                                const eventsSection = document.getElementById('events');
                                if (eventsSection) {
                                    eventsSection.scrollIntoView({ behavior: 'smooth' });
                                }
                            }, 100);
                        }} className="mt-4">
                            Zurück zu Veranstaltungen
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-black text-white py-8">
                <div className="container mx-auto px-4">
                    <Button 
                        variant="ghost" 
                        className="text-white mb-4 hover:bg-white/10"
                        onClick={() => {
                            navigate('/');
                            // Small delay to ensure navigation completes, then scroll to events
                            setTimeout(() => {
                                const eventsSection = document.getElementById('events');
                                if (eventsSection) {
                                    eventsSection.scrollIntoView({ behavior: 'smooth' });
                                }
                            }, 100);
                        }}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Zurück zu Veranstaltungen
                    </Button>
                    <h1 className="text-3xl font-bold">{event?.title}</h1>
                    <div className="flex flex-wrap gap-4 mt-2 text-gray-300">
                        <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(event?.date)}
                        </span>
                        {event?.location && (
                            <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {event.location}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Event-Beschreibung + optionale Flyer-Vorschau */}
            {(event?.description || event?.flyer_url) && (
                <div className="bg-white border-b">
                    <div className="container mx-auto px-4 py-6">
                        <div className="flex flex-col items-center md:flex-row md:items-start gap-6">
                            {event?.flyer_url && (
                                <button
                                    type="button"
                                    onClick={() => setFlyerOpen(true)}
                                    className="shrink-0 self-center md:self-start rounded-sm focus:outline-none focus:ring-2 focus:ring-black/40"
                                    aria-label="Flyer vergrößern"
                                >
                                    <img
                                        src={event.flyer_url}
                                        alt={`Flyer ${event?.title || ''}`}
                                        className="h-[280px] w-auto object-contain rounded-sm shadow-sm hover:opacity-90 transition-opacity"
                                    />
                                </button>
                            )}
                            {event?.description && (
                                <p className="text-gray-700 text-sm md:text-base whitespace-pre-line flex-1 min-w-0">
                                    {event.description}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Progress Steps */}
            <div className="bg-white border-b py-4">
                <div className="container mx-auto px-4">
                    <div className="flex justify-center gap-2 md:gap-8">
                        {(isPhysicalCart
                            ? [
                                { num: 1, label: 'Tickets wählen' },
                                { num: 3, label: 'Kontakt' },
                                { num: 4, label: 'Bezahlung' }
                            ]
                            : [
                                { num: 1, label: 'Tickets wählen' },
                                { num: 2, label: 'Teilnehmer' },
                                { num: 3, label: 'Kontakt' },
                                { num: 4, label: 'Bezahlung' }
                            ]
                        ).map(({ num, label }) => (
                            <div key={num} className={`flex items-center gap-2 ${step >= num ? 'text-black' : 'text-gray-400'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    step >= num ? 'bg-black text-white' : 'bg-gray-200'
                                }`}>
                                    {step > num ? <CheckCircle className="h-4 w-4" /> : num}
                                </div>
                                <span className="hidden md:inline text-sm">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 flex items-center gap-2">
                            <AlertCircle className="h-5 w-5" />
                            {error}
                        </div>
                    )}

                    {/* Step 1: Select Tickets */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <h2 className="text-2xl font-bold mb-6">Tickets auswählen</h2>
                            
                            {tickets.length === 0 ? (
                                <Card>
                                    <CardContent className="p-8 text-center text-gray-500">
                                        <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>Keine Tickets verfügbar</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                tickets.map(ticket => (
                                    <Card key={ticket.id} className={ticket.available_quantity === 0 ? 'opacity-60' : ''}>
                                        <CardContent className="p-4">
                                            <div className="flex justify-between items-center">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-bold text-lg">{ticket.name}</h3>
                                                        {ticket.is_early_bird && (
                                                            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                                                                Early Bird
                                                            </span>
                                                        )}
                                                    </div>
                                                    {ticket.description && (
                                                        <p className="text-gray-600 text-sm mt-1">{ticket.description}</p>
                                                    )}
                                                    <div className="mt-2">
                                                        <span className="text-2xl font-bold">{ticket.current_price?.toFixed(2)} €</span>
                                                        {ticket.is_early_bird && ticket.base_price !== ticket.current_price && (
                                                            <span className="text-gray-400 line-through ml-2">
                                                                {ticket.base_price?.toFixed(2)} €
                                                            </span>
                                                        )}
                                                    </div>
                                                    {ticket.is_on_sale === false ? (
                                                        <p className="text-sm font-medium text-amber-700 mt-1">
                                                            {ticket.sale_reason || 'Noch nicht im Verkauf'}
                                                        </p>
                                                    ) : (
                                                        ticket.available_quantity !== null && (
                                                            <p className="text-sm text-gray-500 mt-1">
                                                                {ticket.available_quantity === 0
                                                                    ? 'Ausverkauft'
                                                                    : `Noch ${ticket.available_quantity} verfügbar`}
                                                            </p>
                                                        )
                                                    )}
                                                </div>
                                                
                                                {ticket.available_quantity !== 0 && ticket.is_on_sale !== false && (
                                                    <div className="flex items-center gap-3">
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => updateQuantity(ticket.id, -1)}
                                                            disabled={!cart[ticket.id]?.quantity}
                                                        >
                                                            <Minus className="h-4 w-4" />
                                                        </Button>
                                                        <span className="w-8 text-center font-bold text-lg">
                                                            {cart[ticket.id]?.quantity || 0}
                                                        </span>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => updateQuantity(ticket.id, 1)}
                                                            disabled={ticket.available_quantity !== null && (cart[ticket.id]?.quantity || 0) >= ticket.available_quantity}
                                                        >
                                                            <Plus className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}

                            {/* Cart Summary */}
                            {getTotalItems() > 0 && (
                                <Card className="bg-black text-white mt-6">
                                    <CardContent className="p-4">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <p className="text-sm text-gray-300">
                                                    <ShoppingCart className="h-4 w-4 inline mr-1" />
                                                    {getTotalItems()} Tickets
                                                </p>
                                                <p className="text-2xl font-bold">{getTotalPrice().toFixed(2)} €</p>
                                            </div>
                                            <Button 
                                                onClick={() => setStep(isPhysicalCart ? 3 : 2)}
                                                className="bg-white text-black hover:bg-gray-100"
                                            >
                                                Weiter
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* Step 2: Attendee Details */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <Button variant="ghost" onClick={() => setStep(1)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Zurück
                                </Button>
                                <h2 className="text-2xl font-bold">Teilnehmer eingeben</h2>
                            </div>
                            
                            {/* Info banner for multiple tickets */}
                            {getTotalItems() > 1 && (
                                <div className="bg-blue-50 border border-blue-200 text-blue-700 p-4 rounded-lg flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium">Hinweis zu mehreren Tickets</p>
                                        <p className="text-sm mt-1">
                                            Jedes Ticket muss für eine andere Person ausgestellt werden. 
                                            Bitte geben Sie für jedes Ticket die Daten der jeweiligen Person ein.
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {/* Logged-in user hint */}
                            {loggedInUser && firstTicketPrefilled && (
                                <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium">Ihr erstes Ticket wurde vorausgefüllt</p>
                                        <p className="text-sm mt-1">
                                            Wir haben Ihre Daten für das erste Ticket automatisch eingetragen.
                                            {getTotalItems() > 1 && ' Bitte geben Sie die Daten für Ihre Begleitung ein.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            {Object.entries(cart).map(([ticketId, item], ticketIdx) => {
                                const ticket = tickets.find(t => t.id === ticketId);
                                // Calculate global attendee index for "is first ticket" check
                                let globalAttendeeBefore = 0;
                                Object.entries(cart).forEach(([tid, titem], tidx) => {
                                    if (tidx < ticketIdx) globalAttendeeBefore += titem.quantity;
                                });
                                
                                return (
                                    <Card key={ticketId}>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-lg flex items-center gap-2">
                                                <Ticket className="h-5 w-5" />
                                                {ticket?.name} ({item.quantity}x)
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {item.attendees.map((att, idx) => {
                                                const isFirstTicketOverall = (globalAttendeeBefore + idx) === 0;
                                                const isPrefilledFirst = isFirstTicketOverall && loggedInUser && firstTicketPrefilled;
                                                
                                                return (
                                                <div key={idx} className={`p-4 rounded-lg ${isPrefilledFirst ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                                                    <p className="font-medium mb-3 flex items-center gap-2">
                                                        <User className="h-4 w-4" />
                                                        Teilnehmer {globalAttendeeBefore + idx + 1}
                                                        {isPrefilledFirst && (
                                                            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full ml-2">
                                                                Ihre Daten
                                                            </span>
                                                        )}
                                                        {!isFirstTicketOverall && getTotalItems() > 1 && (
                                                            <span className="text-xs text-gray-500 ml-2">
                                                                (Begleitung)
                                                            </span>
                                                        )}
                                                    </p>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                        <div>
                                                            <Label>Vorname *</Label>
                                                            <Input
                                                                value={att.first_name}
                                                                onChange={(e) => updateAttendee(ticketId, idx, 'first_name', e.target.value)}
                                                                placeholder="Max"
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>Nachname *</Label>
                                                            <Input
                                                                value={att.last_name}
                                                                onChange={(e) => updateAttendee(ticketId, idx, 'last_name', e.target.value)}
                                                                placeholder="Mustermann"
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label>E-Mail *</Label>
                                                            <Input
                                                                type="email"
                                                                value={att.email}
                                                                onChange={(e) => updateAttendee(ticketId, idx, 'email', e.target.value)}
                                                                placeholder="max@example.de"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                );
                            })}

                            <div className="flex justify-between">
                                <Button variant="outline" onClick={() => setStep(1)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Zurück
                                </Button>
                                <Button 
                                    onClick={() => {
                                        const validation = validateStep2();
                                        if (validation.valid) {
                                            setStep(3);
                                            setError(null);
                                        } else {
                                            setError(validation.error);
                                            toast.error(validation.error);
                                        }
                                    }}
                                    className="bg-black hover:bg-gray-800"
                                >
                                    Weiter
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Customer Info */}
                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <Button variant="ghost" onClick={() => setStep(isPhysicalCart ? 1 : 2)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Zurück
                                </Button>
                                <h2 className="text-2xl font-bold">Ihre Kontaktdaten</h2>
                            </div>
                            
                            <Card>
                                <CardContent className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>Vorname *</Label>
                                            <Input
                                                value={customerInfo.first_name}
                                                onChange={(e) => setCustomerInfo({...customerInfo, first_name: e.target.value})}
                                                placeholder="Max"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label>Nachname *</Label>
                                            <Input
                                                value={customerInfo.last_name}
                                                onChange={(e) => setCustomerInfo({...customerInfo, last_name: e.target.value})}
                                                placeholder="Mustermann"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>E-Mail *</Label>
                                        <Input
                                            type="email"
                                            value={customerInfo.email}
                                            onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                                            placeholder="max@example.de"
                                            required
                                        />
                                        <p className="text-sm text-gray-500 mt-1">
                                            {isPhysicalCart
                                                ? 'An diese Adresse senden wir die Bestellbestätigung mit Abhol-Code (kein Online-/QR-Ticket).'
                                                : 'An diese Adresse werden die Tickets gesendet'}
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Telefon (optional)</Label>
                                        <Input
                                            type="tel"
                                            value={customerInfo.phone}
                                            onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                                            placeholder="+49 123 456789"
                                        />
                                    </div>

                                    {canShip && (
                                        <div className="border-t pt-4 space-y-2">
                                            <Label>Lieferung *</Label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name="delivery" checked={deliveryMethod === 'pickup'} onChange={() => setDeliveryMethod('pickup')} />
                                                <span>Abholung vor Ort (kostenlos)</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name="delivery" checked={deliveryMethod === 'shipping'} onChange={() => setDeliveryMethod('shipping')} />
                                                <span>Versand per Post (+{SHIPPING_FEE.toFixed(2)} € pauschal)</span>
                                            </label>
                                        </div>
                                    )}

                                    {isShipping && (
                                        <div className="space-y-3 border-t pt-4">
                                            {/* DSGVO-Hinweis bei Adresseingabe. Wortlaut mit Alex/Datenschutz abstimmen;
                                                falls ok, ggf. nur die Datenschutzerklärung (/datenschutz) ergänzen, nicht diesen Text. */}
                                            <p className="text-xs text-gray-500 bg-gray-50 border rounded-md p-3">
                                                Deine Adresse wird ausschließlich zum Versand deiner Tickets verwendet und
                                                spätestens eine Woche nach der Veranstaltung automatisch gelöscht. Mehr dazu in unserer{' '}
                                                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-black">
                                                    Datenschutzerklärung
                                                </a>.
                                            </p>
                                            <div>
                                                <Label>Name (Empfänger)</Label>
                                                <Input
                                                    value={shippingAddress.name}
                                                    onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                                                    placeholder="Leer = Käufername"
                                                />
                                            </div>
                                            <div>
                                                <Label>Straße & Hausnr. *</Label>
                                                <Input
                                                    value={shippingAddress.street}
                                                    onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                                                    placeholder="Musterstraße 1"
                                                    required
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Label>PLZ *</Label>
                                                    <Input
                                                        value={shippingAddress.zip}
                                                        onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                                                        placeholder="88453"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Ort *</Label>
                                                    <Input
                                                        value={shippingAddress.city}
                                                        onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                                                        placeholder="Erolzheim"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label>Adresszusatz (optional)</Label>
                                                <Input
                                                    value={shippingAddress.addon}
                                                    onChange={(e) => setShippingAddress({...shippingAddress, addon: e.target.value})}
                                                    placeholder="c/o, Wohnung, ..."
                                                />
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Order Summary */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Zusammenfassung</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {Object.entries(cart).map(([ticketId, item]) => {
                                            const ticket = tickets.find(t => t.id === ticketId);
                                            return (
                                                <div key={ticketId} className="flex justify-between">
                                                    <span>{item.quantity}x {ticket?.name}</span>
                                                    <span>{(ticket?.current_price * item.quantity).toFixed(2)} €</span>
                                                </div>
                                            );
                                        })}
                                        {isShipping && (
                                            <div className="flex justify-between text-sm text-gray-600">
                                                <span>Versand (pauschal)</span>
                                                <span>{SHIPPING_FEE.toFixed(2)} €</span>
                                            </div>
                                        )}
                                        <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                                            <span>Gesamt</span>
                                            <span>{(getTotalPrice() + shippingFee).toFixed(2)} €</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Honeypot: für echte Nutzer unsichtbar — nur Bots füllen es aus.
                                Wird nur im kostenlosen Pfad (register-free) ausgewertet. */}
                            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                                <label htmlFor="website">Website (bitte leer lassen)</label>
                                <input
                                    type="text"
                                    id="website"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                />
                            </div>

                            <div className="flex justify-between">
                                <Button variant="outline" onClick={() => setStep(isPhysicalCart ? 1 : 2)}>
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Zurück
                                </Button>
                                <Button
                                    onClick={handleCheckout}
                                    disabled={!validateStep3() || checkoutLoading}
                                    className="bg-black hover:bg-gray-800"
                                >
                                    {checkoutLoading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Wird verarbeitet...
                                        </>
                                    ) : (
                                        <>
                                            {getTotalPrice() === 0 ? 'Kostenlos anmelden' : 'Zur Zahlung'}
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {flyerOpen && event?.flyer_url && (
                <Lightbox images={[event.flyer_url, event.flyer_url_2].filter(Boolean)} onClose={() => setFlyerOpen(false)} />
            )}
        </div>
    );
};

export default TicketShop;
